import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Table, Tag, Space, DatePicker, Select, Button, Row, Col, List, Empty, Modal, Form, Input, InputNumber, Image, Popconfirm, message } from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import api from '../../api/http';

const { RangePicker } = DatePicker;

const statusConfig = {
  PENDING: { text: '待审批', color: 'processing' },
  APPROVED: { text: '已批准', color: 'success' },
  REJECTED: { text: '已拒绝', color: 'error' },
  WITHDRAWN: { text: '已撤回', color: 'default' },
  DELETED: { text: '已删除', color: 'default' }
};

const typeConfig = {
  LEAVE: { text: '请假', color: 'geekblue' },
  OVERTIME: { text: '加班', color: 'purple' }
};

const MyApplicationsList = ({ refreshKey } = {}) => {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({
    record_type: 'ALL',
    status: '',
    date_range: []
  });
  const [detailVisible, setDetailVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [editForm] = Form.useForm();

  const isMobile = useMemo(() => window.matchMedia && window.matchMedia('(max-width: 768px)').matches, []);

  const leaveTypeMap = {
    annual: '年假',
    sick: '病假',
    personal: '事假',
    marriage: '婚假',
    maternity: '产假',
    paternity: '陪产假',
    compensatory: '调休',
    other: '其他'
  };

  const overtimeTypeMap = {
    weekday: '工作日加班',
    weekend: '周末加班',
    holiday: '节假日加班',
    makeup: '调休补班'
  };

  const leaveTypeOptions = [
    { value: '事假', label: '事假' },
    { value: '调休', label: '调休' },
    { value: '年假', label: '年假' },
    { value: '婚假', label: '婚假' },
    { value: '丧假', label: '丧假' },
    { value: '护产假', label: '护产假' },
    { value: '产假', label: '产假' },
    { value: '病假', label: '病假' },
    { value: '工伤假', label: '工伤假' },
    { value: '其他', label: '其他' },
    ...Object.entries(leaveTypeMap).map(([value, label]) => ({ value, label }))
  ];

  const overtimeTypeOptions = [
    { value: '工作日加班', label: '工作日加班' },
    { value: '周末加班', label: '周末加班' },
    { value: '节假日加班', label: '节假日加班' },
    { value: '调休补班', label: '调休补班' },
    ...Object.entries(overtimeTypeMap).map(([value, label]) => ({ value, label }))
  ];

  const getTypeLabel = (recordType, typeValue) => {
    if (!typeValue) return '—';
    if (recordType === 'LEAVE') {
      return leaveTypeMap[typeValue] || typeValue;
    }
    if (recordType === 'OVERTIME') {
      return overtimeTypeMap[typeValue] || typeValue;
    }
    return typeValue;
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : String(value);
  };

  const fetchDetail = async (record) => {
    if (!record) return null;
    const type = record.record_type === 'LEAVE' ? 'leave' : 'overtime';
    const response = await api.get(`/leave-overtime/my/application/${type}/${record.raw_id}`);
    return response?.data?.data || null;
  };

  const openDetail = async (record) => {
    try {
      setActionLoading(true);
      const detail = await fetchDetail(record);
      setCurrentRecord(record);
      setDetailData(detail);
      setDetailVisible(true);
    } catch (error) {
      message.error('获取申请详情失败');
    } finally {
      setActionLoading(false);
    }
  };

  const openEdit = async (record) => {
    try {
      setActionLoading(true);
      const detail = await fetchDetail(record);
      if (!detail) {
        message.error('获取申请详情失败');
        return;
      }
      setCurrentRecord(record);
      setDetailData(detail);
      editForm.setFieldsValue({
        type: record.record_type === 'LEAVE' ? detail.leave_type : detail.overtime_type,
        date_range: [dayjs(detail.start_date), dayjs(detail.end_date)],
        start_time: detail.start_time ? Number(String(detail.start_time).slice(0, 2)) : undefined,
        end_time: detail.end_time ? Number(String(detail.end_time).slice(0, 2)) : undefined,
        reason: detail.reason,
        work_content: detail.work_content
      });
      setEditVisible(true);
    } catch (error) {
      message.error('打开编辑失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async (values) => {
    if (!currentRecord) return;
    try {
      setActionLoading(true);
      const type = currentRecord.record_type === 'LEAVE' ? 'leave' : 'overtime';
      const payload = {
        start_date: values.date_range[0].format('YYYY-MM-DD'),
        end_date: values.date_range[1].format('YYYY-MM-DD'),
        start_time: values.start_time ? `${String(values.start_time).padStart(2, '0')}:00` : null,
        end_time: values.end_time ? `${String(values.end_time).padStart(2, '0')}:00` : null,
        reason: values.reason,
        attachments: detailData?.attachments || []
      };

      if (currentRecord.record_type === 'LEAVE') {
        payload.leave_type = values.type;
      } else {
        payload.overtime_type = values.type;
        payload.work_content = values.work_content || detailData?.work_content || '';
      }

      const response = await api.put(`/leave-overtime/my/application/${type}/${currentRecord.raw_id}`, payload);
      if (response?.data?.success) {
        message.success('更新成功');
        setEditVisible(false);
        fetchMyApplications();
      } else {
        message.error(response?.data?.message || '更新失败');
      }
    } catch (error) {
      message.error('更新失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWithdraw = async (record) => {
    if (!record) return;
    try {
      setActionLoading(true);
      const type = record.record_type === 'LEAVE' ? 'leave' : 'overtime';
      const response = await api.post(`/leave-overtime/my/application/${type}/${record.raw_id}/withdraw`);
      if (response?.data?.success) {
        message.success('撤回成功');
        fetchMyApplications();
      } else {
        message.error(response?.data?.message || '撤回失败');
      }
    } catch (error) {
      message.error('撤回失败');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (record) => {
    if (!record) return;
    try {
      setActionLoading(true);
      const type = record.record_type === 'LEAVE' ? 'leave' : 'overtime';
      const response = await api.delete(`/leave-overtime/my/application/${type}/${record.raw_id}`);
      if (response?.data?.success) {
        message.success('删除成功');
        fetchMyApplications();
      } else {
        message.error(response?.data?.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    } finally {
      setActionLoading(false);
    }
  };

  const normalizeLeave = (item) => ({
    id: `LEAVE-${item.id}`,
    raw_id: item.id,
    record_type: 'LEAVE',
    type: item.leave_type,
    start_date: item.start_date,
    end_date: item.end_date,
    total_hours: item.total_hours,
    status: item.status,
    apply_time: item.apply_time,
    attachments: item.attachments || []
  });

  const normalizeOvertime = (item) => ({
    id: `OVERTIME-${item.id}`,
    raw_id: item.id,
    record_type: 'OVERTIME',
    type: item.overtime_type,
    start_date: item.start_date,
    end_date: item.end_date,
    total_hours: item.total_hours,
    status: item.status,
    apply_time: item.apply_time,
    attachments: item.attachments || []
  });

  const fetchMyApplications = useCallback(async (paramsOverride = {}) => {
    const nextPagination = paramsOverride.pagination || pagination;
    const nextFilters = paramsOverride.filters || filters;

    const params = {
      status: nextFilters.status || undefined,
      page: nextPagination.current,
      page_size: nextPagination.pageSize
    };

    if (nextFilters.date_range && nextFilters.date_range.length === 2) {
      params.start_date = nextFilters.date_range[0].format('YYYY-MM-DD');
      params.end_date = nextFilters.date_range[1].format('YYYY-MM-DD');
    }

    try {
      setLoading(true);

      if (nextFilters.record_type === 'LEAVE') {
        const response = await api.get('/leave-overtime/leave/my', { params });
        const list = Array.isArray(response?.data?.data) ? response.data.data.map(normalizeLeave) : [];
        setRecords(list);
        setPagination((prev) => {
          const next = {
            current: response?.data?.pagination?.page || nextPagination.current,
            pageSize: response?.data?.pagination?.page_size || nextPagination.pageSize,
            total: response?.data?.pagination?.total || 0
          };
          if (prev.current === next.current && prev.pageSize === next.pageSize && prev.total === next.total) {
            return prev;
          }
          return next;
        });
        return;
      }

      if (nextFilters.record_type === 'OVERTIME') {
        const response = await api.get('/leave-overtime/overtime/my', { params });
        const list = Array.isArray(response?.data?.data) ? response.data.data.map(normalizeOvertime) : [];
        setRecords(list);
        setPagination((prev) => {
          const next = {
            current: response?.data?.pagination?.page || nextPagination.current,
            pageSize: response?.data?.pagination?.page_size || nextPagination.pageSize,
            total: response?.data?.pagination?.total || 0
          };
          if (prev.current === next.current && prev.pageSize === next.pageSize && prev.total === next.total) {
            return prev;
          }
          return next;
        });
        return;
      }

      const [leaveRes, overtimeRes] = await Promise.all([
        api.get('/leave-overtime/leave/my', { params }),
        api.get('/leave-overtime/overtime/my', { params })
      ]);

      const leaveList = Array.isArray(leaveRes?.data?.data) ? leaveRes.data.data.map(normalizeLeave) : [];
      const overtimeList = Array.isArray(overtimeRes?.data?.data) ? overtimeRes.data.data.map(normalizeOvertime) : [];
      const merged = [...leaveList, ...overtimeList].sort((a, b) => {
        const aTime = a.apply_time ? new Date(a.apply_time).getTime() : 0;
        const bTime = b.apply_time ? new Date(b.apply_time).getTime() : 0;
        return bTime - aTime;
      });

      setRecords(merged);
      setPagination((prev) => {
        const next = {
          current: nextPagination.current,
          pageSize: nextPagination.pageSize,
          total: merged.length
        };
        if (prev.current === next.current && prev.pageSize === next.pageSize && prev.total === next.total) {
          return prev;
        }
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  const fetchRef = useRef(fetchMyApplications);
  const paginationRef = useRef(pagination);
  useEffect(() => {
    fetchRef.current = fetchMyApplications;
  }, [fetchMyApplications]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    fetchRef.current();
  }, []);

  useEffect(() => {
    // 提交成功后触发刷新（由父组件递增 refreshKey）
    if (refreshKey === undefined) return;
    fetchRef.current({ pagination: { ...paginationRef.current, current: 1 } });
  }, [refreshKey]);

  const columns = [
    {
      title: '类型',
      dataIndex: 'record_type',
      key: 'record_type',
      render: (value) => {
        const config = typeConfig[value] || { text: value, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '事项',
      dataIndex: 'type',
      key: 'type',
      render: (value, record) => getTypeLabel(record.record_type, value)
    },
    {
      title: '日期范围',
      key: 'date_range',
      render: (_, record) => `${formatDate(record.start_date)} ~ ${formatDate(record.end_date)}`
    },
    {
      title: '时长(小时)',
      dataIndex: 'total_hours',
      key: 'total_hours',
      render: (value) => (value ?? 0)
    },
    {
      title: '审批状态',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const config = statusConfig[value] || { text: value, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '提交时间',
      dataIndex: 'apply_time',
      key: 'apply_time',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-')
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space size={8}>
          <Button type="link" onClick={() => openDetail(record)}>查看</Button>
          <Button type="link" disabled={record.status !== 'PENDING'} onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm
            title="确认撤回该申请？"
            onConfirm={() => handleWithdraw(record)}
            okText="撤回"
            cancelText="取消"
            disabled={record.status !== 'PENDING'}
          >
            <Button type="link" disabled={record.status !== 'PENDING'}>撤回</Button>
          </Popconfirm>
          <Popconfirm
            title="确认删除该申请？"
            onConfirm={() => handleDelete(record)}
            okText="删除"
            cancelText="取消"
          >
            <Button type="link" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleFilterChange = (next) => {
    const newFilters = { ...filters, ...next };
    setFilters(newFilters);
    const nextPagination = { ...pagination, current: 1 };
    setPagination(nextPagination);
    fetchMyApplications({ filters: newFilters, pagination: nextPagination });
  };

  const handleTableChange = (nextPagination) => {
    const updated = { ...pagination, current: nextPagination.current, pageSize: nextPagination.pageSize };
    setPagination(updated);
    fetchMyApplications({ pagination: updated });
  };

  const renderMobileList = () => (
    <List
      dataSource={records}
      locale={{ emptyText: <Empty description="暂无申请记录" /> }}
      renderItem={(item) => {
        const status = statusConfig[item.status] || { text: item.status, color: 'default' };
        const type = typeConfig[item.record_type] || { text: item.record_type, color: 'default' };
        return (
          <List.Item key={item.id} style={{ padding: '12px 0' }}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              <Space size={8} wrap>
                <Tag color={type.color}>{type.text}</Tag>
                <Tag color={status.color}>{status.text}</Tag>
              </Space>
              <div style={{ fontWeight: 600 }}>{getTypeLabel(item.record_type, item.type)}</div>
              <div style={{ color: 'var(--text-secondary)' }}>{formatDate(item.start_date)} ~ {formatDate(item.end_date)}</div>
              <div style={{ color: 'var(--text-secondary)' }}>时长：{item.total_hours ?? 0} 小时</div>
              <div style={{ color: 'var(--text-secondary)' }}>提交时间：{item.apply_time ? dayjs(item.apply_time).format('YYYY-MM-DD HH:mm') : '-'}</div>
              <Space wrap>
                <Button size="small" onClick={() => openDetail(item)}>查看</Button>
                <Button size="small" disabled={item.status !== 'PENDING'} onClick={() => openEdit(item)}>编辑</Button>
                <Popconfirm
                  title="确认撤回该申请？"
                  onConfirm={() => handleWithdraw(item)}
                  okText="撤回"
                  cancelText="取消"
                  disabled={item.status !== 'PENDING'}
                >
                  <Button size="small" disabled={item.status !== 'PENDING'}>撤回</Button>
                </Popconfirm>
                <Popconfirm
                  title="确认删除该申请？"
                  onConfirm={() => handleDelete(item)}
                  okText="删除"
                  cancelText="取消"
                >
                  <Button size="small" danger>删除</Button>
                </Popconfirm>
              </Space>
            </Space>
          </List.Item>
        );
      }}
    />
  );

  return (
    <Card title="我的申请" size="small">
      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={8}>
          <Select
            value={filters.record_type}
            onChange={(value) => handleFilterChange({ record_type: value })}
            style={{ width: '100%' }}
            options={[
              { label: '全部类型', value: 'ALL' },
              { label: '请假', value: 'LEAVE' },
              { label: '加班', value: 'OVERTIME' }
            ]}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Select
            value={filters.status}
            onChange={(value) => handleFilterChange({ status: value })}
            style={{ width: '100%' }}
            allowClear
            placeholder="审批状态"
            options={[
              { label: '待审批', value: 'PENDING' },
              { label: '已批准', value: 'APPROVED' },
              { label: '已拒绝', value: 'REJECTED' },
              { label: '已撤回', value: 'WITHDRAWN' },
              { label: '已删除', value: 'DELETED' }
            ]}
          />
        </Col>
        <Col xs={24} sm={8}>
          <RangePicker
            locale={zhCN}
            style={{ width: '100%' }}
            value={filters.date_range}
            onChange={(value) => handleFilterChange({ date_range: value || [] })}
            placeholder={['开始日期', '结束日期']}
          />
        </Col>
      </Row>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<SearchOutlined />} type="primary" onClick={() => fetchMyApplications()}>
          查询
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => handleFilterChange({ status: '', record_type: 'ALL', date_range: [] })}>
          重置
        </Button>
      </Space>
      {isMobile ? (
        renderMobileList()
      ) : (
        <Table
          rowKey="id"
          loading={loading && records.length === 0}
          dataSource={records}
          columns={columns}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: true }}
        />
      )}
      <Modal
        title="申请详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>关闭</Button>,
          <Button key="edit" type="primary" disabled={!currentRecord || currentRecord.status !== 'PENDING'} onClick={() => { setDetailVisible(false); openEdit(currentRecord); }}>
            编辑
          </Button>
        ]}
        width={720}
      >
        {detailData ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div><strong>类型：</strong>{detailData.leave_type ? '请假' : '加班'}</div>
            <div><strong>申请人：</strong>{detailData.employee_name || '—'}</div>
            <div><strong>事项：</strong>{getTypeLabel(detailData.leave_type ? 'LEAVE' : 'OVERTIME', detailData.leave_type || detailData.overtime_type)}</div>
            <div><strong>日期范围：</strong>{formatDate(detailData.start_date)} ~ {formatDate(detailData.end_date)}</div>
            <div><strong>时长：</strong>{detailData.total_hours ?? 0} 小时</div>
            <div><strong>审批状态：</strong>{statusConfig[detailData.status]?.text || detailData.status}</div>
            <div><strong>原因：</strong>{detailData.reason || '—'}</div>
            {detailData.work_content && <div><strong>工作内容：</strong>{detailData.work_content}</div>}
            <div>
              <strong>附件：</strong>
              {detailData.attachments && detailData.attachments.length ? (
                <Image.PreviewGroup>
                  <Space wrap style={{ marginTop: 8 }}>
                    {detailData.attachments.map((item, index) => {
                      const url = item?.url || item?.thumbUrl || item;
                      if (!url) return null;
                      return (
                        <Image key={`${url}-${index}`} width={80} height={80} src={url} style={{ objectFit: 'cover' }} />
                      );
                    })}
                  </Space>
                </Image.PreviewGroup>
              ) : (
                <span> 无</span>
              )}
            </div>
          </Space>
        ) : (
          <Empty description="暂无详情" />
        )}
      </Modal>
      <Modal
        title="编辑申请"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={() => editForm.submit()}
        confirmLoading={actionLoading}
        width={720}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Form.Item name="type" label="事项" rules={[{ required: true, message: '请选择事项' }]}>
            <Select
              options={currentRecord?.record_type === 'LEAVE' ? leaveTypeOptions : overtimeTypeOptions}
            />
          </Form.Item>
          <Form.Item name="date_range" label="日期范围" rules={[{ required: true, message: '请选择日期范围' }]}>
            <RangePicker locale={zhCN} style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={12}>
              <Form.Item name="start_time" label="开始时间">
                <InputNumber min={0} max={24} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="end_time" label="结束时间">
                <InputNumber min={0} max={24} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          {currentRecord?.record_type === 'OVERTIME' && (
            <Form.Item name="work_content" label="工作内容">
              <Input.TextArea rows={3} />
            </Form.Item>
          )}
          <Form.Item name="reason" label="原因" rules={[{ required: true, message: '请输入原因' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          {detailData?.attachments && detailData.attachments.length ? (
            <Form.Item label="附件">
              <Image.PreviewGroup>
                <Space wrap>
                  {detailData.attachments.map((item, index) => {
                    const url = item?.url || item?.thumbUrl || item;
                    if (!url) return null;
                    return (
                      <Image key={`${url}-${index}`} width={80} height={80} src={url} style={{ objectFit: 'cover' }} />
                    );
                  })}
                </Space>
              </Image.PreviewGroup>
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </Card>
  );
};

export default MyApplicationsList;
